import React, { useEffect, useRef, useState } from 'react';
import { Table } from 'components/molecules/Table';
import { Pagination } from 'components/molecules/Pagination';
import { useAlert } from 'models/alertContext';
import { EPerPages, ISortOptions } from 'models/table';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormModal } from 'components/molecules/Modal';
import { cancelLicencePrintRequest } from 'requests/licence';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useTranslations } from 'hooks/useTranslations';
import { Box } from '@mui/material';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useDictionary } from 'models/dictionaryContext';
import { IPrintoutsSearchForm } from 'models/licences';
import { CardList } from 'components/molecules/CardList';
import { formatDateToRequest } from 'utils/date';
import { renderLicenceRowColor } from 'components/molecules/Table/helpers';
import { useLicences } from './hooks';
import { mapPrintoutsToDataSource, printoutsTableColumns } from './helpers';
import { PrintoutTableCard } from './PrintoutTableCard';
import { PrintoutsSearchForm } from './PrintoutsSearchForm';
import { useLang } from 'models/langContext';

const PrintoutsPage = () => {
  const { nextPageAlert, samePageAlert, setAlert, clearAlert } = useAlert();
  const [printoutToCancel, setPrintoutToCancel] = useState<{
    id: string;
    version: number;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [printoutQuery, setPrintoutQuery] = useState<Partial<IPrintoutsSearchForm>>({});
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const [isLoader, setLoader] = useState(true);
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const scrollPosition = useRef(0);
  const { weaponLicencePrintType, getWeaponLicencePrintType } = useDictionary();
  const {
    getPrintoutsSearchData,
    getWeaponLicencePrintStatus,
    weaponLicencePrintStatus,
    printouts,
    printoutsPaginator,
  } = useLicences();

  useEffect(() => {
    if (!isLoader) clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
  }, [isLoader]);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    getWeaponLicencePrintStatus();
    getWeaponLicencePrintType();
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getDataInit() {
      await getPrintoutsSearchData(currentPage, perPage, printoutQuery, sort);
      setLoader(false);
      setIsSortLoaded(true);
    })();
  }, [currentPage, perPage, printoutQuery, sort]);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  const handleSetLicenceQuery = (values: Partial<IPrintoutsSearchForm>) => {
    setCurrentPage(1);
    values.requestDateFrom = values.requestDateFrom
      ? formatDateToRequest(values.requestDateFrom, true, true)
      : null;
    values.requestDateTo = values.requestDateTo
      ? formatDateToRequest(values.requestDateTo, true, true)
      : null;
    setPrintoutQuery(values);
  };

  const handleCancel = (id: string, version: number) => setPrintoutToCancel({ id, version });
  const handleRetry = (id: string) => {};

  const handleConfirmCancel = async () => {
    if (!printoutToCancel) {
      return;
    }
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);

    let success: boolean;
    let errorMessage = '';

    try {
      success = await cancelLicencePrintRequest(printoutToCancel.id, printoutToCancel.version);
    } catch (e: any) {
      success = false;
      errorMessage =
        e?.response?.data?.message || e?.response?.data?.errors?.[0]?.message || '';
    }

    setPrintoutToCancel(null);

    setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
      text: success
        ? t('printLicence.cancel.success.alert')
        : errorMessage || t('printLicence.cancel.error.alert'),
      variant: success ? EAlertVariants.success : EAlertVariants.error,
    });

    if (success) {
      await getPrintoutsSearchData(currentPage, perPage, printoutQuery, sort);
    }
  };

  return (
    <>
      {nextPageAlert && (
        <Box sx={{ mb: 2, mt: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}

      {samePageAlert && (
        <Box sx={{ mb: 2, mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}

      <PrintoutsSearchForm onSubmit={handleSetLicenceQuery} />

      {isLoader ? (
        <Loader isLoading={isLoader} />
      ) : (
        <div>
          {isDesktop ? (
            <Table
              columns={printoutsTableColumns({
                t,
                handleCancel,
                handleRetry,
              })}
              dataSource={mapPrintoutsToDataSource(
                printouts,
                weaponLicencePrintStatus,
                longDateFormat,
                weaponLicencePrintType,
              )}
              onSort={(it) => {
                setSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              specificRowColor={renderLicenceRowColor}
              specificRowColorHover={renderLicenceRowColor}
              loading={isLoader}
            />
          ) : (
            <CardList
              items={mapPrintoutsToDataSource(
                printouts,
                weaponLicencePrintStatus,
                longDateFormat,
                weaponLicencePrintType,
              )}
              render={(data, index) => (
                <PrintoutTableCard
                  data={data}
                  handleCancel={handleCancel}
                  handleRetry={handleRetry}
                />
              )}
            />
          )}
        </div>
      )}
      {printoutsPaginator && (
        <Pagination
          count={printoutsPaginator?.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={false}
          isVisible={isSortLoaded}
        />
      )}

      <FormModal
        open={!!printoutToCancel}
        titleLeft={true}
        title={t('printLicence.cancel.modal.title')}
        text={t('printLicence.cancel.modal.message.text')}
        approveButtonLabel={t('general.yes.label')}
        cancelButtonLabel={t('general.no.label')}
        onClickApprove={handleConfirmCancel}
        onClickClose={() => setPrintoutToCancel(null)}
      />
    </>
  );
};

export default PrintoutsPage;
